import { Box, Image as ChakraImage } from "@chakra-ui/react";
import Head from "next/head";
import Link from "next/link";
import React from "react";
import * as S from "../../styles/SellingForUsers";

function SellingForProfessionals() {
  return (<>
    <Head>
      <title key="title">Selling on Zonado for Professionals</title>
      <meta
        key="description"
        name="description"
        content="Learn about the benefits of using Zonado as a frequent seller and how you can use it to reach interested buyers in Canada."
      />
      <meta
        key="og:title"
        property="og:title"
        content="Selling on Zonado for Professionals"
      />
      <meta
        key="og:description"
        property="og:description"
        content="Learn about the benefits of using Zonado as a frequent seller and how you can use it to reach interested buyers in Canada."
      />
    </Head>
    <S.SellingForUsersPageWrapper>
      <S.BannerImageContainer
        imageUrl={
          "/images/selling/professionals/selling-for-professionals.jpg"
        }
      >
        <S.BannerHeadingText>
          Selling on Zonado for Professionals
        </S.BannerHeadingText>
        <S.BannerBlurb>
          Learn about the benefits of using Zonado as a frequent seller and
          how you can use it to reach interested buyers in Canada.
        </S.BannerBlurb>
      </S.BannerImageContainer>
      <S.Text>
        Professionals often call Zonado ‘a breath of fresh air’. In an
        industry where trying to sell a commercial real estate or business
        listing can mean spending hours in dozens of different places, Zonado
        gives you a streamlined tool to move your clients’ properties, without
        all the hassle.
      </S.Text>
      <S.Text>
        Professionals (real estate agents, business brokers, auctioneers) use
        Zonado because it allows them to access a focused, clutter-free
        marketplace where serious buyers can find their listings easier.
      </S.Text>
      <S.Text>
        Zonado makes it easy for professionals to manage their inventory of
        listings, get detailed analytics on their performance, communicate
        with interested parties in real time, and move more listings faster.
      </S.Text>

      <S.HeadingText>Benefits of listing on Zonado</S.HeadingText>
      <S.Text>
        In the past few years, professionals have made Zonado a go-to resource
        for when they need to move commercial real estate or business
        listings. There are a few key reasons why Zonado is loved and trusted
        by professionals:
      </S.Text>

      <S.SubHeadingText>Listed until sold</S.SubHeadingText>
      <S.SubHeadingDescription>
        One of the key benefits of listing on Zonado is that we do not force time limits on listings. Your listing will remain live on Zonado until it is marked sold{" "}
      </S.SubHeadingDescription>

      <S.SubHeadingText>Access to a Growing Community</S.SubHeadingText>
      <S.SubHeadingDescription>
        As a professional, you have the opportunity to tap into a growing
        national community of thousands of Canadian investors, business
        owners, and other professionals who use Zonado to browse, buy or sell
        listings every month. We personalize buyers' experience on Zonado to
        ensure that your listings are being shown to people in your area who
        are actively searching for the types of properties that you are
        selling.
      </S.SubHeadingDescription>

      <S.SubHeadingText>List in a network built on trust</S.SubHeadingText>
      <S.SubHeadingDescription>
        The most important way in which Zonado is different from other listing
        and ad sites is that we value the trust of our users. We do not permit
        unauthentic listings. This includes listings that are ‘daisy chains’
        where the agent posting the property does not have the authority to
        market it. This also includes ‘lead gen’ listings with vague details
        meant to honey trap leads. This means that the quality of other
        listings next to yours help your listing, don’t harm it.
      </S.SubHeadingDescription>

      <S.SubHeadingText>Ease of Use</S.SubHeadingText>
      <S.SubHeadingDescription>
        Zonado was built with people in mind. It’s fast and simple to use. As
        a frequent seller, you can create new listings in a matter of minutes,
        respond to inquiries within a few seconds and update listing
        availability within moments of selling a listing. These tools allow
        you to sell more listings in less time and provide a better buying
        experience to your clients.
      </S.SubHeadingDescription>

      <S.SubHeadingText>Real-Time Communication</S.SubHeadingText>
      <S.SubHeadingDescription>
        On Zonado, professionals can engage with interested buyers using
        Zonado Messaging. When a buyer is interested in a listing, they can
        use Zonado Messaging to reach out directly to you with any questions
        or concerns they might have. Send multiple files, see whether your
        message has been read and get SMS alerts whenever you have a new
        message.
      </S.SubHeadingDescription>

      <S.SubHeadingText>Advanced Analytics</S.SubHeadingText>
      <S.SubHeadingDescription>
        Sellers on Zonado get detailed insights about their listing’s
        performance. See who has been engaging with the listing the most to
        find promising prospects. Know who has interacted with your listing
        flyer and the number of visits your listing has received from Zonado.
      </S.SubHeadingDescription>

      <Box my="70px">
        <ChakraImage src="/images/selling/professionals/dashboard-screenshot.png" />
      </Box>

      <S.HeadingText>Promoting Your Listings</S.HeadingText>
      <S.Text>
        As a professional, you can promote listings and increase exposure in
        one of two ways on Zonado:
      </S.Text>

      <S.SubHeadingText>Sharing Listings Organically</S.SubHeadingText>
      <S.SubHeadingDescription>
        One great way to get your listing in front of more interested buyers
        is by simply sharing your listing on your social media profiles and
        pages, posting about it in groups you’ve joined or emailing it to your
        mailing list.
      </S.SubHeadingDescription>

      <S.SubHeadingText>Boosting Listings</S.SubHeadingText>
      <S.SubHeadingDescription>
        Another way to increase visibility on your listing is by boosting it
        after it has been created. Boosting your Zonado listing gives it up to
        20 times more exposure on the platfrom and helps you rise above the
        crowd. Contact{" "}
        <S.LinkText href="mailto:hello@zonado.com">
          hello@zonado.com
        </S.LinkText>{" "}
        to learn how to boost your listing.
      </S.SubHeadingDescription>

      <S.BannerImageContainer
        imageUrl="/images/selling/professionals/footer-image.jpg"
        style={{
          marginTop: "100px",
        }}
      >
        <S.BannerHeadingText>Buy or sell on Zonado</S.BannerHeadingText>
        <Link href="https://zonado.com">

          <S.CTAButton>Go to Zonado</S.CTAButton>

        </Link>
      </S.BannerImageContainer>
    </S.SellingForUsersPageWrapper>
  </>);
}
SellingForProfessionals.newLayout = true;

export default SellingForProfessionals;
